import { LogUtils } from "./log-utils";
// Update the import path below to the correct relative or package path where csrfToken utilities are defined
import { getValidCsrfToken, clearCsrfToken } from "./csrfToken";

interface FetchDataRequestOptions {
  method: "GET" | "POST" | "PUT" | "DELETE";
  skipCredentials?: boolean;
  skipCsrf?: boolean;
}

interface FetchDataRequest {
  endpoint: string;
  cache?: RequestCache;
  options?: FetchDataRequestOptions;
  params?: string | Record<string, string | number> | string[][] | URLSearchParams | undefined;
  body?: unknown;
  headers?: Record<string, string>;
  tags?: string[];
  revalidate?: number;
}

// Helper to build headers
function buildHeaders(customHeaders?: Record<string, string>) {
  return {
    "content-type": "application/json",
    Accept: "application/json",
    ...(customHeaders || {}),
  };
}

async function parseResponseBody(response: Response) {
  if (typeof response.arrayBuffer !== "function") {
    const contentType = response.headers?.get("content-type");
    if (contentType?.includes("application/json")) return response.json();

    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  let bytes = await response.arrayBuffer();
  const body = new Uint8Array(bytes);

  // Some upstreams return a gzip payload without the Content-Encoding header.
  if (body[0] === 0x1f && body[1] === 0x8b) {
    const decompressed = new Blob([body]).stream().pipeThrough(new DecompressionStream("gzip"));
    bytes = await new Response(decompressed).arrayBuffer();
  }

  const text = new TextDecoder().decode(bytes);
  const contentType = response.headers?.get("content-type");
  if (contentType?.includes("application/json")) return JSON.parse(text);

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Helper to build endpoint with params
function buildRequestEndpoint(endpoint: string, params?: FetchDataRequest["params"]) {
  if (!params || Object.keys(params).length === 0) return endpoint;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  return decodeURIComponent(`${endpoint}?${searchParams.toString()}`);
}

// Helper to inject CSRF token for POST
async function injectCsrfToken(body: unknown, forceRefresh: boolean) {
  const csrfToken = await getValidCsrfToken(forceRefresh); // getValidCsrfToken always checks sessionStorage
  const newBody: any = body && typeof body === "object" ? { ...body } : {};
  if (csrfToken) newBody._csrf = csrfToken;
  return newBody;
}

interface FetchWithCsrfRetryRequest {
  endpoint: string;
  body: unknown;
  options?: FetchDataRequestOptions;
  headers: Record<string, string>;
  fetchOptions?: Omit<RequestInit, "body" | "method" | "headers">;
}

interface FetchWithCsrfRetryOptions {
  request: FetchWithCsrfRetryRequest;
  cache: RequestCache;
  tags?: string[];
  revalidate?: number;
}

// Helper to perform fetch with retry for POST/CSRF
async function fetchWithCsrfRetry({ request, cache, tags, revalidate }: FetchWithCsrfRetryOptions) {
  let triedRefresh = false;
  let response: Response;
  do {
    const newBody = await injectCsrfToken(request.body, triedRefresh);
    response = await fetch(request.endpoint, {
      method: "POST",
      credentials: "include",
      cache,
      ...(tags && { next: { tags } }),
      ...(revalidate && { next: { revalidate } }),
      body: JSON.stringify(newBody),
      ...(request.fetchOptions || {}),
      ...(request.options || {}),
      headers: request.headers,
    });
    if (
      !response.ok &&
      (response.status === 401 || response.status === 404 || response.status === 403) &&
      !triedRefresh
    ) {
      clearCsrfToken();
      triedRefresh = true;
      continue;
    }
    break;
  } while (true);
  return response;
}

export const fetchData = async (request: FetchDataRequest) => {
  try {
    const {
      endpoint,
      params,
      options,
      body,
      tags,
      cache = "force-cache",
      revalidate,
      headers: customHeaders,
    } = request;

    const methodType = options?.method ?? "GET";
    const skipCsrf = options?.skipCsrf ?? false;
    const skipCredentials = options?.skipCredentials || "include";
    const hasRequestBody = body ? Object.keys(body).length > 0 : false;
    const requestEndpoint = buildRequestEndpoint(endpoint, params);
    const headers = buildHeaders(customHeaders);

    let response: Response;
    if (methodType.toUpperCase() === "POST" && !skipCsrf) {
      response = await fetchWithCsrfRetry({
        request: {
          endpoint: requestEndpoint,
          body,
          options,
          headers,
        },
        cache,
        tags,
        revalidate,
      });
    } else {
      response = await fetch(requestEndpoint, {
        method: methodType,
        ...(!skipCredentials ? { credentials: "include" } : {}),
        cache,
        ...(tags && { next: { tags } }),
        ...(revalidate && { next: { revalidate } }),
        ...(methodType !== "GET" && hasRequestBody && { body: JSON.stringify(body) }),
        ...options,
        headers,
      });
    }
    const data = await parseResponseBody(response);
    if (response.ok) {
      return data;
    } else {
      return null;
    }
  } catch (error: unknown) {
    LogUtils.error(`Error fetching data from : ${request.endpoint}`, error);
  }
};
